import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  BoardField,
  CardReminderXData,
  CardTimelineEvent,
  CardTimelineEventAction,
  CardTimelineEventType,
  CardTimelineReminderEvent,
} from '../board-v3/board-v3.types';
import { BoardV3RealtimeService } from '../board-v3/board-v3-realtime.service';
import {
  TimelineCompanyChange,
  TimelineContactChange,
} from '../board-v3/card-detail/card-timeline/timeline-update/timeline-update.component';
import { TIMELINE_ENTITY_TYPE, TimelineEvent } from '../../interfaces/timeline.interface';
import { TimelineService } from '../../services/timeline.service';
import { StackedSidebarService } from '../../services/stacked-sidebar.service';
import { BoardContactService } from '../../services/board-contact.service';
import { BoardCompanyService } from '../../services/board-company.service';
import { TaskService } from '../../services/task.service';
import { SidebarType } from '../layout/stacked-sidebar/stacked-sidebar.interface';
import { FUNNEL_TYPE } from '../../enum/constants';
import { formatFullName } from '../../helpers';

export type TimelineFilterKey = 'all' | 'history' | 'calls' | 'comments' | 'reminders';

/** Every tab; a host shows the ones it needs through `filters` */
export const TIMELINE_FILTERS: TimelineFilterKey[] = ['all', 'history', 'calls', 'comments', 'reminders'];

const FILTER_OPTIONS: Record<TimelineFilterKey, { labelKey: string; eventTypes?: CardTimelineEventType[] }> = {
  all: { labelKey: 'board_v3_tab_timeline_all' },
  history: {
    labelKey: 'board_v3_tab_timeline_history',
    eventTypes: [
      CardTimelineEventType.SYSTEM,
      CardTimelineEventType.CARD,
      CardTimelineEventType.CONTACT,
      CardTimelineEventType.COMPANY,
      CardTimelineEventType.CHAT,
      CardTimelineEventType.TASK,
    ],
  },
  calls: { labelKey: 'calls_tab', eventTypes: [CardTimelineEventType.CALL] },
  comments: { labelKey: 'board_v3_tab_timeline_comments', eventTypes: [CardTimelineEventType.COMMENT] },
  reminders: { labelKey: 'board_v3_tab_timeline_reminders', eventTypes: [CardTimelineEventType.REMINDER] },
};

/** The entity an event is about, as shown above it in a timeline of several entities */
export interface TimelineEntityLink {
  icon: string;
  typeKey: string;
  name: string;
  /** False when the entity is deleted or can't be opened from here */
  canOpen: boolean;
  deleted: boolean;
}

const ENTITY_VIEW: Record<string, { icon: string; typeKey: string }> = {
  [TIMELINE_ENTITY_TYPE.CARD]: { icon: 'view_kanban', typeKey: 'card' },
  [TIMELINE_ENTITY_TYPE.CONTACT]: { icon: 'person', typeKey: 'contact' },
  [TIMELINE_ENTITY_TYPE.COMPANY]: { icon: 'apartment', typeKey: 'company' },
  [TIMELINE_ENTITY_TYPE.CHAT]: { icon: 'chat', typeKey: 'timeline_entity_chat' },
  [TIMELINE_ENTITY_TYPE.TASK]: { icon: 'task_alt', typeKey: 'timeline_entity_task' },
};

const PAGE_SIZE = 50;
/** `take` limit of `GET /timeline` */
const MAX_TAKE = 200;

/**
 * The timeline of any entity, all from `GET /timeline` in one format: pass `entityType` + `entityId` for a card,
 * contact, company or chat, or `createdBy` for everything a user did. Loads by pages, keeps itself up to date
 * through the socket and draws every event type.
 */
@Component({
  selector: 'app-timeline',
  templateUrl: './timeline.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TimelineComponent implements OnChanges {
  private readonly timelineService = inject(TimelineService);
  private readonly realtimeService = inject(BoardV3RealtimeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stackedSidebarService = inject(StackedSidebarService);
  private readonly contactService = inject(BoardContactService);
  private readonly companyService = inject(BoardCompanyService);
  private readonly taskService = inject(TaskService);

  @Input() entityType?: TIMELINE_ENTITY_TYPE | `${TIMELINE_ENTITY_TYPE}`;
  @Input() entityId?: string | null;
  /** Everything this user did, across all the entities */
  @Input() createdBy?: string | null;
  /** Tabs above the list; none or one hides them */
  @Input() filters: TimelineFilterKey[] = [];
  /** Off when the host already titles the timeline */
  @Input() showHeader = true;
  @Input() fieldDefinitions?: Map<string, BoardField>;
  @Input() cardCurrency?: string | null;
  /** What each event is about, above it; on by default when the timeline is not of one entity (e.g. of a user) */
  @Input() showEntity?: boolean;
  @Input() canPrefillReminders = true;
  @Output() repeatReminder = new EventEmitter<CardReminderXData>();
  @Output() editReminder = new EventEmitter<CardReminderXData>();
  @Output() contactChanged = new EventEmitter<TimelineContactChange>();
  @Output() companyChanged = new EventEmitter<TimelineCompanyChange>();

  protected readonly CardTimelineEventType = CardTimelineEventType;
  protected readonly FILTER_OPTIONS = FILTER_OPTIONS;
  protected activeFilter: TimelineFilterKey = 'all';
  /** Newest first */
  protected events: CardTimelineEvent[] = [];
  protected total = 0;
  protected loading = false;
  protected loaded = false;
  private page = 0;
  private request?: Subscription;
  private realtime?: Subscription;

  protected get hasMore(): boolean {
    return this.events.length < this.total;
  }

  private get activeEventTypes(): CardTimelineEventType[] | undefined {
    return FILTER_OPTIONS[this.activeFilter].eventTypes;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entityType'] || changes['entityId'] || changes['createdBy']) {
      this.activeFilter = 'all';
      this.subscribeToRealtime();
      this.reset();
    }
  }

  /** Loads the events again, as many as are shown now (e.g. after a comment or a change of the entity) */
  public reload(): void {
    if (!this.hasSource()) return;

    const take = Math.min(Math.max(this.events.length, PAGE_SIZE), MAX_TAKE);
    this.load(1, take, true);
  }

  protected selectFilter(filter: TimelineFilterKey): void {
    if (this.activeFilter === filter) return;

    this.activeFilter = filter;
    this.reset();
  }

  protected loadMore(): void {
    if (!this.loading && this.hasMore) this.load(this.page + 1, PAGE_SIZE, false);
  }

  /** The card of a card event: its comments and reminders are changed through the card */
  protected getCardId(event: CardTimelineEvent): string {
    return event.card_id ?? '';
  }

  protected get isEntityShown(): boolean {
    return this.showEntity ?? !this.entityId;
  }

  protected getEntityLink(event: CardTimelineEvent): TimelineEntityLink | null {
    const view = event.entity_type ? ENTITY_VIEW[event.entity_type] : undefined;
    if (!view || !event.entity_id) return null;

    const entity = event.entity;
    const isCard = event.entity_type === TIMELINE_ENTITY_TYPE.CARD;
    const typeKey = isCard && entity?.funnel_type ? entity.funnel_type.toLowerCase() : view.typeKey;
    const name = entity?.name || formatFullName(entity?.first_name, entity?.last_name);
    const deleted = entity?.active === false;
    // A second card detail would replace the shared `BoardService.card` of the open one
    const blocked = (isCard || event.entity_type === TIMELINE_ENTITY_TYPE.TASK) && this.isCardDetailOpen();

    return { icon: view.icon, typeKey, name, deleted, canOpen: !deleted && !blocked };
  }

  protected openEntity(event: CardTimelineEvent): void {
    if (!this.getEntityLink(event)?.canOpen) return;

    const id = event.entity_id!;
    const canOpenCards = !this.isCardDetailOpen();
    const opened$: Observable<unknown> | null = (() => {
      switch (event.entity_type) {
        case TIMELINE_ENTITY_TYPE.CARD:
          return this.stackedSidebarService.openSidebar(
            { type: SidebarType.CardV3, width: '90vw' },
            { card_id: id, type: event.entity?.funnel_type === FUNNEL_TYPE.LEAD ? FUNNEL_TYPE.LEAD : FUNNEL_TYPE.DEAL },
          );
        case TIMELINE_ENTITY_TYPE.CONTACT:
          return this.contactService.openContactDetail(id, canOpenCards);
        case TIMELINE_ENTITY_TYPE.COMPANY:
          return this.companyService.openCompanyDetail(id, canOpenCards);
        case TIMELINE_ENTITY_TYPE.CHAT:
          return this.stackedSidebarService.openChats(id);
        case TIMELINE_ENTITY_TYPE.TASK:
          return this.taskService.openTaskDetail(id);
        default:
          return null;
      }
    })();

    opened$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private isCardDetailOpen(): boolean {
    return this.stackedSidebarService.isOpen(SidebarType.CardV3);
  }

  private reset(): void {
    this.request?.unsubscribe();
    this.events = [];
    this.total = 0;
    this.page = 0;
    this.loaded = false;
    if (this.hasSource()) this.load(1, PAGE_SIZE, true);
  }

  private hasSource(): boolean {
    return !!(this.entityId || this.createdBy);
  }

  /** `take` of a reload can be bigger than a page, the next pages go on from where it ends */
  private load(page: number, take: number, replace: boolean): void {
    this.request?.unsubscribe();
    this.loading = true;
    this.request = this.timelineService
      .getEvents({
        entity_type: this.entityType as TIMELINE_ENTITY_TYPE | undefined,
        entity_id: this.entityId ?? undefined,
        created_by: this.createdBy ?? undefined,
        event_type: this.activeEventTypes,
        page,
        take,
      })
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          const items = (response?.items ?? []).map((event) => this.toCardTimelineEvent(event));
          this.events = replace ? items : this.mergeById(this.events, items);
          this.total = Math.max(response?.total ?? 0, this.events.length);
          // After a bigger reload the next page may overlap a little, the events are deduped by id
          this.page = replace ? Math.floor(take / PAGE_SIZE) : page;
          this.loaded = true;
        },
        error: () => (this.loaded = true),
      });
  }

  private subscribeToRealtime(): void {
    this.realtime?.unsubscribe();
    this.realtime = undefined;
    if (!this.entityType || !this.entityId) return;

    const events$: Observable<TimelineEvent[]> =
      this.entityType === TIMELINE_ENTITY_TYPE.CHAT
        ? this.realtimeService.chatTimelineEvent$(this.entityId).pipe(map(({ events }) => events))
        : this.realtimeService
            .timelineEvent$(this.entityType, this.entityId)
            .pipe(map(({ timelines }) => timelines as unknown as TimelineEvent[]));

    this.realtime = events$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((events) => this.applyRealtimeEvents(events.map((event) => this.toCardTimelineEvent(event))));
  }

  /** A known event is replaced, a reminder state goes into its reminder, the rest is added on top */
  private applyRealtimeEvents(events: CardTimelineEvent[]): void {
    const allowedTypes = this.activeEventTypes;
    const incoming = allowedTypes ? events.filter((event) => allowedTypes.includes(event.event_type)) : events;
    if (!incoming.length) return;

    const next = [...this.events];
    const fresh: CardTimelineEvent[] = [];

    for (const event of incoming) {
      const existingIndex = next.findIndex((item) => item.id === event.id);
      if (existingIndex !== -1) {
        next[existingIndex] = event;
        continue;
      }

      if (this.isReminderStateEvent(event)) {
        const reminderId = event.x_data?.card_reminder_id;
        if (!reminderId) continue;

        const reminderIndex = next.findIndex(
          (item) => this.isReminderStateEvent(item) && item.x_data?.card_reminder_id === reminderId,
        );
        const existingReminder = next[reminderIndex];
        if (existingReminder && this.isReminderStateEvent(existingReminder)) {
          next[reminderIndex] = { ...existingReminder, x_data: { ...existingReminder.x_data, ...event.x_data } };
          continue;
        }
      }

      fresh.push(event);
    }

    this.events = this.sortNewestFirst([...fresh, ...next]);
    this.total += fresh.length;
  }

  private mergeById(current: CardTimelineEvent[], incoming: CardTimelineEvent[]): CardTimelineEvent[] {
    const byId = new Map(current.map((event) => [event.id, event]));
    for (const event of incoming) byId.set(event.id, event);
    return this.sortNewestFirst([...byId.values()]);
  }

  private sortNewestFirst(events: CardTimelineEvent[]): CardTimelineEvent[] {
    return events.sort((a, b) => b.created_at.localeCompare(a.created_at));
  }

  private isReminderStateEvent(event: CardTimelineEvent): event is CardTimelineReminderEvent {
    return event.event_type === CardTimelineEventType.REMINDER && event.action !== CardTimelineEventAction.FIRED;
  }

  /** One format for every entity; the card of a card event is its `entity_id`, a system event has no author */
  private toCardTimelineEvent(event: TimelineEvent): CardTimelineEvent {
    const user = event.created_by_user;
    return {
      ...event,
      card_id: event.entity_type === TIMELINE_ENTITY_TYPE.CARD ? event.entity_id : undefined,
      created_by_user: user?.id ? { ...user, id: user.id } : null,
    } as CardTimelineEvent;
  }
}
